import React, { useEffect, useMemo, useRef, useState } from "react";
import { actionLabel, roleLabel, roleClass } from "../roles";

function findPlayer(players, id) {
  return players.find((p) => String(p.id) === String(id)) || null;
}

// Розкладає повний журнал завершеної гри на кроки по раундах — так, як
// ведучий проживав їх наживо: спершу нічні дії (вбивство звіряється з
// лікуванням), потім денне голосування. Кожен крок несе список тих, хто
// вибув саме цього раунду, щоб можна було відтворити партію раунд за
// раундом, а не показувати одразу фінальний стан.
function buildRoundSteps(actions) {
  if (!actions || actions.length === 0) return [];
  const maxRound = actions.reduce((m, a) => Math.max(m, Number(a.round) || 1), 1);

  const steps = [];
  for (let r = 1; r <= maxRound; r++) {
    const roundActions = actions.filter((a) => Number(a.round) === r);
    const nightActions = roundActions.filter((a) => a.phase !== "day");
    const dayActions = roundActions.filter((a) => a.phase === "day");

    const healedIds = new Set(
      nightActions
        .filter((a) => a.type === "heal" && a.targetId != null)
        .map((a) => String(a.targetId))
    );
    const nightDeaths = [
      ...new Set(
        nightActions
          .filter((a) => a.type === "kill" && a.targetId != null)
          .map((a) => String(a.targetId))
          .filter((id) => !healedIds.has(id))
      ),
    ];
    const dayDeaths = [
      ...new Set(
        dayActions
          .filter((a) => a.type === "vote_out" && a.targetId != null)
          .map((a) => String(a.targetId))
      ),
    ];

    steps.push({ round: r, nightActions, dayActions, nightDeaths, dayDeaths });
  }
  return steps;
}

function ReplayActionRow({ action, players, delayMs = 0 }) {
  const actor = findPlayer(players, action.actorId);
  const target = findPlayer(players, action.targetId);
  return (
    <li className="action-log-row replay-action-row" style={{ animationDelay: `${delayMs}ms` }}>
      <span className="action-log-body">
        <strong>{actionLabel(action.type)}</strong>
        {actor && (
          <>
            {" "}
            · {actor.name} <span className={roleClass(actor.role)}>({roleLabel(actor.role)})</span>
          </>
        )}
        {target && (
          <>
            {" "}
            → {target.name}{" "}
            <span className={roleClass(target.role)}>({roleLabel(target.role)})</span>
          </>
        )}
        {action.note && <span className="action-log-note"> — {action.note}</span>}
      </span>
    </li>
  );
}

const AUTOPLAY_DELAY_MS = 2200;

export default function GameReplay({ players, actions }) {
  const steps = useMemo(() => buildRoundSteps(actions), [actions]);

  // index 0 = стан "до гри" (усі живі, нічого ще не сталось);
  // index N (1..steps.length) = стан одразу після раунду N.
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef(null);

  const isStart = index === 0;
  const isEnd = index >= steps.length;

  useEffect(() => {
    if (!playing) return undefined;
    if (isEnd) {
      setPlaying(false);
      return undefined;
    }
    timerRef.current = setTimeout(() => setIndex((i) => i + 1), AUTOPLAY_DELAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [playing, index, isEnd]);

  // Кумулятивний набір id тих, хто вже вибув до кожного кроку включно.
  const deadByStep = useMemo(() => {
    const acc = [new Set()];
    const dead = new Set();
    for (const s of steps) {
      s.nightDeaths.forEach((id) => dead.add(id));
      s.dayDeaths.forEach((id) => dead.add(id));
      acc.push(new Set(dead));
    }
    return acc;
  }, [steps]);

  if (steps.length === 0) {
    return <p className="hint-text">У цій грі немає записаних дій для відтворення.</p>;
  }

  const deadNow = deadByStep[index] || new Set();
  const livePlayers = players.map((p) => ({ ...p, alive: !deadNow.has(String(p.id)) }));
  const currentStep = isStart ? null : steps[index - 1];

  function goTo(i) {
    setPlaying(false);
    setIndex(Math.max(0, Math.min(steps.length, i)));
  }

  function togglePlay() {
    if (isEnd) {
      setIndex(0);
      setPlaying(true);
      return;
    }
    setPlaying((p) => !p);
  }

  return (
    <div className="replay">
      <div className="replay-controls">
        <button
          type="button"
          className="btn btn-ghost btn-small"
          onClick={() => goTo(index - 1)}
          disabled={isStart}
        >
          ← Раунд назад
        </button>
        <button type="button" className="btn btn-toggle btn-toggle-active replay-play" onClick={togglePlay}>
          {playing ? "⏸ Пауза" : isEnd ? "▶ Спочатку" : "▶ Грати"}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-small"
          onClick={() => goTo(index + 1)}
          disabled={isEnd}
        >
          Раунд вперед →
        </button>
      </div>

      <div className="replay-progress-track">
        <div
          key={`${index}-${playing}-${isEnd}`}
          className={`replay-progress-fill ${playing && !isEnd ? "replay-progress-fill-active" : ""}`}
        />
      </div>

      <div className="replay-scrubber" role="tablist" aria-label="Раунди партії">
        {Array.from({ length: steps.length + 1 }, (_, i) => (
          <button
            key={i}
            type="button"
            className={`replay-dot ${i === index ? "replay-dot-active" : ""} ${
              i < index ? "replay-dot-past" : ""
            }`}
            onClick={() => goTo(i)}
            aria-label={i === 0 ? "Початок гри" : `Після раунду ${i}`}
            aria-current={i === index ? "step" : undefined}
          >
            {i === 0 ? "0" : i}
          </button>
        ))}
      </div>

      <div key={index} className="replay-step">
        <h3 className="replay-heading">
          {isStart ? "Початок гри · усі живі" : `Після раунду ${currentStep.round}`}
        </h3>

        <div className="replay-round-summary">
          {isStart && <p className="hint-text">Гравці ще не встигли зробити жодного ходу.</p>}
          {currentStep && (
            <>
              {currentStep.nightActions.length > 0 && (
                <div className="replay-phase-block">
                  <p className="replay-phase-label">Ніч</p>
                  <ul className="action-log">
                    {currentStep.nightActions.map((a, i) => (
                      <ReplayActionRow
                        key={a.id}
                        action={a}
                        players={players}
                        delayMs={i * 90}
                      />
                    ))}
                  </ul>
                </div>
              )}
              {currentStep.dayActions.length > 0 && (
                <div className="replay-phase-block">
                  <p className="replay-phase-label">День</p>
                  <ul className="action-log">
                    {currentStep.dayActions.map((a, i) => (
                      <ReplayActionRow
                        key={a.id}
                        action={a}
                        players={players}
                        delayMs={(currentStep.nightActions.length + i) * 90}
                      />
                    ))}
                  </ul>
                </div>
              )}
              {currentStep.nightDeaths.length === 0 && currentStep.dayDeaths.length === 0 && (
                <p className="hint-text">Цього раунду ніхто не вибув.</p>
              )}
            </>
          )}
        </div>

        <ul className="player-board">
          {livePlayers.map((p, i) => {
            const justDied =
              currentStep &&
              (currentStep.nightDeaths.includes(String(p.id)) ||
                currentStep.dayDeaths.includes(String(p.id)));
            return (
              <li
                key={p.id}
                className={`player-card replay-card ${p.alive ? "" : "player-card-out"} ${
                  justDied ? "replay-just-died" : ""
                }`}
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div className="player-card-main">
                  <span className="player-card-name">{p.name}</span>
                  <span className={`player-card-role ${roleClass(p.role)}`}>{roleLabel(p.role)}</span>
                </div>
                <span className="player-out-badge">
                  {p.alive ? "живий" : justDied ? "☠ вибув щойно" : "поза грою"}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
